<template>
  <div ref="root" class="date-range-picker">
    <button
      v-if="!inline"
      type="button"
      class="date-range-trigger"
      :class="{ 'date-range-trigger--open': isOpen, 'date-range-trigger--disabled': disabled }"
      :disabled="disabled"
      :aria-disabled="disabled ? 'true' : 'false'"
      @click.stop="toggleOpen"
    >
      <span class="date-range-trigger-main">
        <i v-if="leadingIcon" :class="['fas', leadingIcon, 'date-range-leading-icon']" aria-hidden="true"></i>
        <span class="date-range-trigger-text">{{ displayText }}</span>
      </span>
      <i :class="['fas', isOpen ? 'fa-chevron-up' : 'fa-chevron-down', 'date-range-trigger-icon']"></i>
    </button>

    <div v-if="inline || isOpen" class="date-range-dropdown" :class="{ 'date-range-dropdown--inline': inline }" @click.stop>
      <div v-if="allowMonths" class="picker-mode">
        <button
          type="button"
          :class="['picker-mode-btn', { 'picker-mode-btn--active': pickerMode === 'months' }]"
          @click="setPickerMode('months')"
        >
          Meses
        </button>
        <button
          type="button"
          :class="['picker-mode-btn', { 'picker-mode-btn--active': pickerMode === 'days' }]"
          @click="setPickerMode('days')"
        >
          Dias
        </button>
      </div>

      <div class="calendar-nav" :class="{ 'calendar-nav--year-only': allowMonths && pickerMode === 'months' }">
        <select v-model.number="viewYear" class="calendar-nav-select" @change="onViewChange">
          <option v-for="year in yearOptions" :key="year" :value="year">{{ year }}</option>
        </select>
        <select
          v-if="!allowMonths || pickerMode === 'days'"
          v-model.number="viewMonth"
          class="calendar-nav-select"
          @change="onMonthChange"
        >
          <option v-for="month in monthSelectOptions" :key="month.value" :value="month.value">
            {{ month.label }}
          </option>
        </select>
      </div>

      <div v-if="allowMonths && pickerMode === 'months'" class="month-pick-panel">
        <button
          type="button"
          :class="['month-pick', 'month-pick--all', { 'month-pick--selected': allMonthsChosen }]"
          @click="toggleAllChosenMonths"
        >
          Todos
        </button>
        <div class="month-pick-grid">
          <button
            v-for="month in monthChoices"
            :key="month.value"
            type="button"
            :class="['month-pick', { 'month-pick--selected': isMonthChosen(month.value) }]"
            @click="toggleChosenMonth(month.value)"
          >
            {{ month.short }}
          </button>
        </div>
      </div>

      <div v-else-if="isAllMonthsSelected" class="calendar-all-months">
        <i class="fas fa-calendar-check calendar-all-months-icon"></i>
        <p class="calendar-all-months-title">Todos os meses de {{ viewYear }}</p>
        <p class="calendar-all-months-range">
          {{ formatDisplay(draftStart) }} — {{ formatDisplay(draftEnd) }}
        </p>
      </div>

      <template v-else>
      <div class="calendar-weekdays">
        <span v-for="weekday in weekdays" :key="weekday">{{ weekday }}</span>
      </div>

      <div class="calendar-grid">
        <button
          v-for="day in calendarDays"
          :key="day.date"
          type="button"
          :class="dayClasses(day)"
          :disabled="isDisabled(day.date)"
          @click="onDayClick(day.date)"
        >
          {{ day.day }}
        </button>
      </div>
      </template>

      <div class="calendar-footer">
        <span class="calendar-hint">{{ selectionHint }}</span>
        <div class="calendar-actions">
          <button type="button" class="calendar-btn calendar-btn--ghost" @click="clearSelection">
            Limpar
          </button>
          <button
            type="button"
            class="calendar-btn calendar-btn--primary"
            :disabled="!canApply || disabled"
            @click="applySelection"
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

moment.locale('pt-br')

const ALL_MONTHS_VALUE = 0

const MONTHS = [
  { value: 1, label: 'Janeiro', short: 'Jan' },
  { value: 2, label: 'Fevereiro', short: 'Fev' },
  { value: 3, label: 'Março', short: 'Mar' },
  { value: 4, label: 'Abril', short: 'Abr' },
  { value: 5, label: 'Maio', short: 'Mai' },
  { value: 6, label: 'Junho', short: 'Jun' },
  { value: 7, label: 'Julho', short: 'Jul' },
  { value: 8, label: 'Agosto', short: 'Ago' },
  { value: 9, label: 'Setembro', short: 'Set' },
  { value: 10, label: 'Outubro', short: 'Out' },
  { value: 11, label: 'Novembro', short: 'Nov' },
  { value: 12, label: 'Dezembro', short: 'Dez' }
]

export default {
  name: 'DateRangePicker',
  props: {
    startDate: {
      type: String,
      default: null
    },
    endDate: {
      type: String,
      default: null
    },
    minDate: {
      type: String,
      default: null
    },
    maxDate: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: 'Selecione o período'
    },
    leadingIcon: {
      type: String,
      default: ''
    },
    allowMonths: {
      type: Boolean,
      default: false
    },
    inline: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    const today = moment()
    return {
      isOpen: false,
      viewYear: today.year(),
      viewMonth: today.month() + 1,
      draftStart: null,
      draftEnd: null,
      pickerMode: 'days',
      selectedMonths: [],
      weekdays: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
    }
  },
  computed: {
    monthSelectOptions () {
      return [
        { value: ALL_MONTHS_VALUE, label: 'Todos' },
        ...MONTHS
      ]
    },
    isAllMonthsSelected () {
      return this.viewMonth === ALL_MONTHS_VALUE
    },
    monthChoices () {
      return MONTHS
    },
    allMonthsChosen () {
      return this.selectedMonths.length === MONTHS.length
    },
    monthSelectionLabel () {
      const sorted = [...this.selectedMonths].sort((a, b) => a - b)
      if (!sorted.length) return ''
      const names = sorted.map(value => MONTHS.find(month => month.value === value).label)
      if (names.length === MONTHS.length) return `Todos os meses de ${this.viewYear}`
      if (names.length === 1) return `${names[0]} de ${this.viewYear}`
      const contiguous = sorted.every((month, index) => index === 0 || month === sorted[index - 1] + 1)
      if (contiguous) return `${names[0]} a ${names[names.length - 1]} de ${this.viewYear}`
      if (names.length === 2) return `${names[0]} e ${names[1]} de ${this.viewYear}`
      return `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]} de ${this.viewYear}`
    },
    yearOptions () {
      const currentYear = moment().year()
      const years = []
      for (let year = currentYear - 5; year <= currentYear + 1; year++) {
        years.push(year)
      }
      return years
    },
    displayText () {
      if (this.startDate && this.endDate) {
        if (this.startDate === this.endDate) {
          return this.formatDisplay(this.startDate)
        }
        return `${this.formatDisplay(this.startDate)} — ${this.formatDisplay(this.endDate)}`
      }
      return this.placeholder
    },
    calendarDays () {
      if (this.isAllMonthsSelected) return []

      const monthStart = moment({ year: this.viewYear, month: this.viewMonth - 1, day: 1 })
      const gridStart = monthStart.clone().startOf('week')
      const days = []

      for (let i = 0; i < 42; i++) {
        const current = gridStart.clone().add(i, 'days')
        days.push({
          date: current.format('YYYY-MM-DD'),
          day: current.date(),
          isCurrentMonth: current.month() === this.viewMonth - 1
        })
      }

      return days
    },
    selectionHint () {
      if (this.allowMonths && this.pickerMode === 'months') {
        return this.monthSelectionLabel || 'Selecione um ou mais meses'
      }
      if (this.isAllMonthsSelected && this.draftStart && this.draftEnd) {
        return `Ano ${this.viewYear} inteiro selecionado`
      }
      if (!this.draftStart && !this.draftEnd) {
        return 'Selecione a data inicial'
      }
      if (this.draftStart && !this.draftEnd) {
        return `Dia ${this.formatDisplay(this.draftStart)} — clique em Aplicar ou escolha outra data`
      }
      return `${this.formatDisplay(this.draftStart)} — ${this.formatDisplay(this.draftEnd)}`
    },
    canApply () {
      return Boolean(this.draftStart)
    }
  },
  watch: {
    startDate: {
      immediate: true,
      handler () {
        this.syncDraftFromProps()
      }
    },
    endDate () {
      this.syncDraftFromProps()
    },
    disabled (isDisabled) {
      if (isDisabled) this.close()
    }
  },
  mounted () {
    if (this.inline && this.allowMonths) this.pickerMode = 'months'
    document.addEventListener('click', this.onDocumentClick)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onDocumentClick)
  },
  methods: {
    formatDisplay (value) {
      return moment(value).format('DD/MM/YYYY')
    },
    syncDraftFromProps () {
      this.draftStart = this.startDate
      this.draftEnd = this.endDate
      if (this.startDate && this.endDate && this.isFullYearRange(this.startDate, this.endDate)) {
        this.viewYear = moment(this.startDate).year()
        this.viewMonth = ALL_MONTHS_VALUE
        return
      }
      if (this.startDate) {
        const start = moment(this.startDate)
        this.viewYear = start.year()
        this.viewMonth = start.month() + 1
      }
    },
    toggleOpen () {
      if (this.disabled) return
      this.isOpen = !this.isOpen
      if (this.isOpen) {
        this.syncDraftFromProps()
        if (this.allowMonths) {
          this.pickerMode = 'months'
          this.selectedMonths = []
          this.draftStart = null
          this.draftEnd = null
        }
        if (!this.draftStart) {
          const today = moment()
          this.viewYear = today.year()
          this.viewMonth = today.month() + 1
        }
      }
    },
    close () {
      this.isOpen = false
    },
    onDocumentClick (event) {
      if (!this.isOpen) return
      if (this.$refs.root && !this.$refs.root.contains(event.target)) {
        this.close()
      }
    },
    onViewChange () {
      if (this.allowMonths && this.pickerMode === 'months') {
        this.syncRangeFromSelectedMonths()
        return
      }
      if (this.isAllMonthsSelected) {
        this.applyFullYearRange()
      }
    },
    onMonthChange () {
      if (this.allowMonths && this.pickerMode === 'days' && !this.isAllMonthsSelected) return
      if (this.isAllMonthsSelected) {
        this.applyFullYearRange()
        return
      }
      this.draftStart = null
      this.draftEnd = null
    },
    setPickerMode (mode) {
      if (this.pickerMode === mode) return
      this.pickerMode = mode
      this.selectedMonths = []
      this.draftStart = null
      this.draftEnd = null
      if (mode === 'days' && this.isAllMonthsSelected) {
        this.viewMonth = moment().month() + 1
      }
    },
    isMonthChosen (month) {
      return this.selectedMonths.includes(month)
    },
    toggleChosenMonth (month) {
      if (this.disabled) return
      if (this.isMonthChosen(month)) {
        this.selectedMonths = this.selectedMonths.filter(value => value !== month)
      } else {
        this.selectedMonths = [...this.selectedMonths, month]
      }
      this.syncRangeFromSelectedMonths()
    },
    toggleAllChosenMonths () {
      if (this.disabled) return
      this.selectedMonths = this.allMonthsChosen ? [] : MONTHS.map(month => month.value)
      this.syncRangeFromSelectedMonths()
    },
    syncRangeFromSelectedMonths () {
      const months = [...this.selectedMonths].sort((a, b) => a - b)
      if (!months.length) {
        this.draftStart = null
        this.draftEnd = null
        return
      }
      this.draftStart = moment({ year: this.viewYear, month: months[0] - 1, day: 1 }).format('YYYY-MM-DD')
      this.draftEnd = moment({ year: this.viewYear, month: months[months.length - 1] - 1, day: 1 })
        .endOf('month')
        .format('YYYY-MM-DD')
    },
    isFullYearRange (start, end) {
      if (!start || !end) return false
      const startMoment = moment(start)
      const endMoment = moment(end)
      return startMoment.year() === endMoment.year() &&
        startMoment.format('MM-DD') === '01-01' &&
        endMoment.format('MM-DD') === '12-31'
    },
    applyFullYearRange () {
      let start = moment({ year: this.viewYear, month: 0, day: 1 })
      let end = moment({ year: this.viewYear, month: 11, day: 31 })

      if (this.minDate) {
        const min = moment(this.minDate)
        if (start.isBefore(min, 'day')) start = min.clone()
      }
      if (this.maxDate) {
        const max = moment(this.maxDate)
        if (end.isAfter(max, 'day')) end = max.clone()
      }

      this.draftStart = start.format('YYYY-MM-DD')
      this.draftEnd = end.format('YYYY-MM-DD')
    },
    isDisabled (date) {
      if (this.minDate && date < this.minDate) return true
      if (this.maxDate && date > this.maxDate) return true
      return false
    },
    isInRange (date) {
      if (!this.draftStart || !this.draftEnd) return false
      const start = moment(this.draftStart)
      const end = moment(this.draftEnd)
      const current = moment(date)
      return current.isBetween(start, end, 'day', '[]')
    },
    dayClasses (day) {
      const isSingleDay = this.draftStart && !this.draftEnd
      const isStart = this.draftStart === day.date
      const isEnd = this.draftEnd === day.date || (isSingleDay && isStart)
      const inRange = this.isInRange(day.date)

      return {
        'calendar-day': true,
        'calendar-day--outside': !day.isCurrentMonth,
        'calendar-day--start': isStart && !isSingleDay,
        'calendar-day--end': isEnd && !isSingleDay,
        'calendar-day--in-range': inRange && !isStart && !isEnd,
        'calendar-day--selected': isStart || isEnd
      }
    },
    onDayClick (date) {
      if (this.disabled || this.isDisabled(date)) return

      if (!this.draftStart || (this.draftStart && this.draftEnd)) {
        this.draftStart = date
        this.draftEnd = null
        return
      }

      if (date === this.draftStart) {
        this.draftEnd = date
        return
      }

      if (moment(date).isBefore(this.draftStart)) {
        this.draftEnd = this.draftStart
        this.draftStart = date
      } else {
        this.draftEnd = date
      }
    },
    clearSelection () {
      this.draftStart = null
      this.draftEnd = null
      this.selectedMonths = []
      this.viewMonth = moment().month() + 1
      this.$emit('update:startDate', null)
      this.$emit('update:endDate', null)
    },
    applySelection () {
      if (!this.draftStart) return
      const endDate = this.draftEnd || this.draftStart
      const payload = { startDate: this.draftStart, endDate }
      if (this.allowMonths && this.pickerMode === 'months') {
        payload.months = [...this.selectedMonths].sort((a, b) => a - b)
        payload.year = this.viewYear
      }
      this.$emit('update:startDate', this.draftStart)
      this.$emit('update:endDate', endDate)
      this.$emit('apply', payload)
      this.close()
    }
  }
}
</script>

<style scoped>
.date-range-picker {
  position: relative;
  width: 100%;
}

.date-range-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 48px;
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  background: #ffffff;
  color: #2c3e50;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
}

.date-range-trigger:hover:not(:disabled),
.date-range-trigger--open {
  border-color: #c5cdd3;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.date-range-trigger--disabled,
.date-range-trigger:disabled {
  background: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
  box-shadow: none;
}

.date-range-trigger-main {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.date-range-leading-icon {
  flex-shrink: 0;
  color: #FF8C42;
  font-size: 0.9rem;
}

.date-range-trigger-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.date-range-trigger-icon {
  flex-shrink: 0;
  color: #9aa3ab;
  font-size: 0.8rem;
}

.date-range-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 1100;
  width: 320px;
  max-width: calc(100vw - 32px);
  padding: 16px;
  background: #ffffff;
  border: 1px solid #e8ecef;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.14);
}

.date-range-dropdown--inline {
  position: static;
  width: 100%;
  max-width: none;
  padding: 0;
  border: none;
  border-radius: 0;
  box-shadow: none;
}

.picker-mode {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  margin-bottom: 12px;
  padding: 4px;
  background: #f3f4f6;
  border-radius: 10px;
}

.picker-mode-btn {
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #6b7280;
  font-size: 0.85rem;
  font-weight: 700;
  padding: 8px 10px;
  cursor: pointer;
}

.picker-mode-btn--active {
  background: #fff;
  color: #FF7A29;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.month-pick-panel {
  margin-bottom: 14px;
}

.month-pick--all {
  width: 100%;
  margin-bottom: 8px;
}

.month-pick-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.month-pick {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  color: #2c3e50;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 8px 4px;
  cursor: pointer;
}

.month-pick:hover {
  border-color: #FF8C42;
  color: #FF7A29;
}

.month-pick--selected {
  background: #FF8C42;
  border-color: #FF8C42;
  color: #fff;
}

.month-pick--selected:hover {
  color: #fff;
}

.calendar-nav {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 14px;
}

.calendar-nav--year-only {
  grid-template-columns: 1fr;
}

.calendar-nav-select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background: #fff;
  color: #2c3e50;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
}

.calendar-nav-select:focus {
  outline: none;
  border-color: #FF8C42;
  box-shadow: 0 0 0 3px rgba(255, 140, 66, 0.15);
}

.calendar-all-months {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 200px;
  padding: 24px 16px;
  background: linear-gradient(135deg, #fff9f5 0%, #fff1e8 100%);
  border-radius: 12px;
  text-align: center;
}

.calendar-all-months-icon {
  font-size: 1.75rem;
  color: #FF8C42;
  margin-bottom: 4px;
}

.calendar-all-months-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #2c3e50;
}

.calendar-all-months-range {
  margin: 0;
  font-size: 0.88rem;
  color: #6c757d;
  font-weight: 500;
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 8px;
}

.calendar-weekdays span {
  text-align: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: #9aa3ab;
  text-transform: uppercase;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.calendar-day {
  height: 36px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #2c3e50;
  font-size: 0.88rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}

.calendar-day:hover:not(:disabled) {
  background: #f1f3f5;
}

.calendar-day--outside {
  color: #ced4da;
}

.calendar-day--in-range {
  background: #fff1e8;
  color: #d9480f;
  border-radius: 0;
}

.calendar-day--selected,
.calendar-day--start,
.calendar-day--end {
  background: linear-gradient(135deg, #FF8C42 0%, #FF7A29 100%);
  color: #ffffff;
  font-weight: 700;
}

.calendar-day--start {
  border-radius: 8px 0 0 8px;
}

.calendar-day--end {
  border-radius: 0 8px 8px 0;
}

.calendar-day--start.calendar-day--end {
  border-radius: 8px;
}

.calendar-day:disabled {
  color: #dee2e6;
  cursor: not-allowed;
}

.calendar-footer {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.calendar-hint {
  display: block;
  margin-bottom: 10px;
  font-size: 0.82rem;
  color: #6c757d;
}

.calendar-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.calendar-btn {
  border: none;
  border-radius: 10px;
  padding: 8px 14px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
}

.calendar-btn--ghost {
  background: #f1f3f5;
  color: #495057;
}

.calendar-btn--primary {
  background: linear-gradient(135deg, #FF8C42 0%, #FF7A29 100%);
  color: #ffffff;
}

.calendar-btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .date-range-dropdown {
    left: 50%;
    transform: translateX(-50%);
    width: min(320px, calc(100vw - 24px));
  }
}
</style>
